import React, { createContext, useState, useContext, useEffect } from 'react';
import { useApp } from './AppContext';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: Date;
}

export interface Streak {
  currentStreak: number;
  bestStreak: number;
  lastCompletedDate?: Date;
}

interface AchievementContextType {
  achievements: Achievement[];
  unlockAchievement: (id: string) => void;
  streak: Streak;
  incrementStreak: () => void;
  resetStreak: () => void;
  motivationLevel: 'low' | 'medium' | 'high';
  setMotivationLevel: (level: 'low' | 'medium' | 'high') => void;
  motivationMessages: string[];
  getMotivationMessage: () => string;
}

const AchievementContext = createContext<AchievementContextType | undefined>(undefined);

const STREAK_KEY = 'ai-tasky-streak-data';
const ACHIEVEMENTS_KEY = 'ai-tasky-achievements-data';
const MOTIVATION_LEVEL_KEY = 'ai-tasky-motivation-level';

// Default achievements
const defaultAchievements: Achievement[] = [
  {
    id: 'first-task',
    name: 'First Steps',
    description: 'Complete your first task',
    icon: 'check-circle',
    unlocked: false,
  },
  {
    id: 'three-day-streak',
    name: 'On a Roll',
    description: 'Complete at least one task for 3 days in a row',
    icon: 'trending-up',
    unlocked: false,
  },
  {
    id: 'seven-day-streak',
    name: 'Week Warrior',
    description: 'Complete at least one task for 7 days in a row',
    icon: 'award',
    unlocked: false,
  },
  {
    id: 'complete-10-tasks',
    name: 'Productivity Master',
    description: 'Complete 10 tasks in total',
    icon: 'zap',
    unlocked: false,
  },
  {
    id: 'all-categories',
    name: 'Well Rounded',
    description: 'Complete at least one task in each category',
    icon: 'circle',
    unlocked: false,
  },
];

export const useAchievements = () => {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within an AchievementProvider');
  }
  return context;
};

interface AchievementProviderProps {
  children: React.ReactNode;
}

export const AchievementProvider: React.FC<AchievementProviderProps> = ({ children }) => {
  const { tasks } = useApp();
  
  // Achievements state
  const [achievements, setAchievements] = useState<Achievement[]>(() => {
    const storedAchievements = localStorage.getItem(ACHIEVEMENTS_KEY);
    return storedAchievements ? JSON.parse(storedAchievements) : defaultAchievements;
  });
  
  // Streak state
  const [streak, setStreak] = useState<Streak>(() => {
    const storedStreak = localStorage.getItem(STREAK_KEY);
    return storedStreak ? JSON.parse(storedStreak) : {
      currentStreak: 0,
      bestStreak: 0,
    };
  });
  
  // Motivation level (controls frequency and intensity of motivational messages)
  const [motivationLevel, setMotivationLevel] = useState<'low' | 'medium' | 'high'>(() => {
    return (localStorage.getItem(MOTIVATION_LEVEL_KEY) as 'low' | 'medium' | 'high') || 'medium';
  });
  
  // Motivation messages grouped by intensity
  const motivationMessages = {
    low: [
      "Task completed.",
      "Done.",
      "Finished.",
      "Task marked as complete.",
      "Completed."
    ],
    medium: [
      "Nice job completing that task!",
      "Well done!",
      "Great work!",
      "You're making progress!",
      "Keep it up!",
      "Another task down!"
    ],
    high: [
      "Amazing work! You're crushing it today!",
      "Incredible job! Your productivity is inspiring!",
      "Fantastic! You're unstoppable today!",
      "Brilliant work! You're achieving great things today!",
      "You're on fire! Keep this momentum going!",
      "Superb job! You're achieving great things today!"
    ]
  };
  
  // Save to localStorage when states change
  useEffect(() => {
    localStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(achievements));
  }, [achievements]);
  
  useEffect(() => {
    localStorage.setItem(STREAK_KEY, JSON.stringify(streak));
  }, [streak]);
  
  useEffect(() => {
    localStorage.setItem(MOTIVATION_LEVEL_KEY, motivationLevel);
  }, [motivationLevel]);
  
  // Check achievements based on tasks
  useEffect(() => {
    const completedTasks = tasks.filter(task => task.completed);
    const completedTasksCount = completedTasks.length;
    
    // Check "First Steps" achievement
    if (completedTasksCount > 0 && !achievements.find(a => a.id === 'first-task')?.unlocked) {
      unlockAchievement('first-task');
    }
    
    // Check "Productivity Master" achievement
    if (completedTasksCount >= 10 && !achievements.find(a => a.id === 'complete-10-tasks')?.unlocked) {
      unlockAchievement('complete-10-tasks');
    }
    
    // Check "Well Rounded" achievement
    const completedCategories = new Set(completedTasks.map(task => task.category));
    if (
      completedCategories.has('personal') &&
      completedCategories.has('work') &&
      completedCategories.has('health') &&
      completedCategories.has('learning') &&
      !achievements.find(a => a.id === 'all-categories')?.unlocked
    ) {
      unlockAchievement('all-categories');
    }
  }, [tasks]);
  
  // Unlock an achievement
  const unlockAchievement = (id: string) => {
    setAchievements(prevAchievements =>
      prevAchievements.map(achievement =>
        achievement.id === id
          ? { ...achievement, unlocked: true, unlockedAt: new Date() }
          : achievement
      )
    );
  };
  
  // Increment streak (call this when a task is completed)
  const incrementStreak = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    setStreak(prev => {
      // Check if the streak was already updated today
      if (prev.lastCompletedDate) {
        const lastCompleted = new Date(prev.lastCompletedDate);
        lastCompleted.setHours(0, 0, 0, 0);
        
        if (today.getTime() === lastCompleted.getTime()) {
          // Already updated today, don't increment again
          return prev;
        }
        
        // Check if this is consecutive day
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (lastCompleted.getTime() === yesterday.getTime()) {
          // Consecutive day, increment streak
          const newCurrentStreak = prev.currentStreak + 1;
          
          // Check for streak achievements
          if (newCurrentStreak === 3) {
            unlockAchievement('three-day-streak');
          }
          
          if (newCurrentStreak === 7) {
            unlockAchievement('seven-day-streak');
          }
          
          return {
            currentStreak: newCurrentStreak,
            bestStreak: Math.max(newCurrentStreak, prev.bestStreak),
            lastCompletedDate: today
          };
        }
        
        // Not consecutive, reset streak
        return {
          currentStreak: 1,
          bestStreak: prev.bestStreak,
          lastCompletedDate: today
        };
      }
      
      // First completed task ever
      return {
        currentStreak: 1,
        bestStreak: Math.max(1, prev.bestStreak),
        lastCompletedDate: today
      };
    });
  };
  
  // Reset streak (called if user misses a day)
  const resetStreak = () => {
    setStreak(prev => ({
      currentStreak: 0,
      bestStreak: prev.bestStreak,
      lastCompletedDate: undefined
    }));
  };
  
  // Get a random motivation message based on current level
  const getMotivationMessage = () => {
    const messages = motivationMessages[motivationLevel];
    return messages[Math.floor(Math.random() * messages.length)];
  };
  
  return (
    <AchievementContext.Provider
      value={{
        achievements,
        unlockAchievement,
        streak,
        incrementStreak,
        resetStreak,
        motivationLevel,
        setMotivationLevel,
        motivationMessages: motivationMessages[motivationLevel],
        getMotivationMessage
      }}
    >
      {children}
    </AchievementContext.Provider>
  );
};
